import { Component } from 'react';
import PropTypes from 'prop-types';
import * as Sentry from '@sentry/react';
import { aiqLogger } from 'services/aiqLogger';

class RootErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, info) {
        try {
            aiqLogger.log(
                {
                    message: `[RootErrorBoundary] ${error?.message || error}`,
                    componentStack: info?.componentStack,
                    stack: error?.stack,
                },
                'react-error-boundary',
                'error'
            );
        } catch {
            // never let logging break the boundary itself
        }
        Sentry.captureException(error, {
            tags: { boundary: 'root' },
            extra: { componentStack: info?.componentStack },
        });
    }

    handleReload = () => {
        window.location.reload();
    };

    handleGoHome = () => {
        window.location.assign('/');
    };

    render() {
        if (!this.state.hasError) return this.props.children;

        return (
            <div
                role="alert"
                style={{
                    minHeight: '100vh',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '2rem',
                    textAlign: 'center',
                    backgroundColor: '#fafafa',
                    color: '#222',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                }}
            >
                <h1 style={{ fontSize: '1.5rem', marginBottom: '0.75rem' }}>Something went wrong</h1>
                <p style={{ maxWidth: 560, lineHeight: 1.5 }}>
                    AIQ encountered an unexpected error and could not continue. The error has been reported automatically. Please reload the page to
                    continue working.
                </p>
                <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
                    <button
                        type="button"
                        onClick={this.handleReload}
                        style={{
                            padding: '8px 18px',
                            border: 'none',
                            borderRadius: 4,
                            backgroundColor: '#1565c0',
                            color: '#fff',
                            cursor: 'pointer',
                            fontSize: '0.95rem',
                        }}
                    >
                        Reload page
                    </button>
                    <button
                        type="button"
                        onClick={this.handleGoHome}
                        style={{
                            padding: '8px 18px',
                            border: '1px solid #1565c0',
                            borderRadius: 4,
                            backgroundColor: '#fff',
                            color: '#1565c0',
                            cursor: 'pointer',
                            fontSize: '0.95rem',
                        }}
                    >
                        Go to home
                    </button>
                </div>
                {this.state.error && (
                    <pre
                        style={{
                            marginTop: 24,
                            maxWidth: '90vw',
                            maxHeight: '40vh',
                            overflow: 'auto',
                            background: '#fff',
                            border: '1px solid #ddd',
                            padding: 12,
                            fontSize: 12,
                            textAlign: 'left',
                        }}
                    >
                        {String(this.state.error?.stack || this.state.error?.message || this.state.error)}
                    </pre>
                )}
            </div>
        );
    }
}

RootErrorBoundary.propTypes = {
    children: PropTypes.node,
};

export default RootErrorBoundary;
